// Library Methods
import { useEffect, useState, useCallback } from "react";

// Services
import {
  getProgressionAnalysisData,
  getProgressionAnalysisProcessInfo,
} from "src/services/exams/api";

// Constants
import { ReportStatus } from "src/constants/enums";

// Types
import { GPADataType, GPAProcessInfoType } from "src/services/exams/types";

// Hooks
import useTokenAccess from "../auth/useTokenAccess";

const useGPAGeneration = ({
  patientId,
  clinicId,
  analysisId,
  enabled = true,
}: {
  patientId: string;
  clinicId: string;
  analysisId: string;
  enabled?: boolean;
}) => {
  const { getToken } = useTokenAccess();

  const [processInfo, setProcessInfo] = useState<
    GPAProcessInfoType | undefined
  >();
  const [analysisData, setAnalysisData] = useState<GPADataType | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);

  const getProcessInfo = useCallback(
    async (signal: AbortSignal) => {
      const token = await getToken();

      const res = await getProgressionAnalysisProcessInfo(
        token,
        clinicId,
        patientId,
        analysisId,
        signal
      );

      setProcessInfo(res);
    },
    [clinicId, patientId, analysisId, getToken]
  );

  const getGPAData = useCallback(
    async (signal: AbortSignal) => {
      const token = await getToken();

      const res = await getProgressionAnalysisData(
        token,
        clinicId,
        patientId,
        analysisId,
        signal
      );

      const parsedRes = JSON.parse(res);
      setAnalysisData(parsedRes);
    },
    [clinicId, patientId, analysisId, getToken]
  );

  // Check continuously if analysis data is ready or not
  // Since report page gets open
  useEffect(() => {
    const controller = new AbortController();

    const intervalTime = 3000; // Interval between requests (3 seconds)

    const intervalId = setInterval(async () => {
      if (!enabled) {
        setIsLoading(false);
        setIsError(true);
        clearInterval(intervalId);
        return;
      }
      if (processInfo && processInfo.status === ReportStatus.Ready) {
        try {
          await getGPAData(controller.signal);
          clearInterval(intervalId);
          setIsLoading(false);
        } catch (e) {
          // Disregard a canceled error; let the next tick retry.
          if ((e as Error).message === "canceled") return;

          setIsError(true);
          setIsLoading(false);
          clearInterval(intervalId);
        }
        return;
      } else if (processInfo && processInfo.status === ReportStatus.Skipped) {
        setIsSkipped(true);
        setIsLoading(false);
        clearInterval(intervalId);
        return;
      } else if (
        processInfo &&
        (processInfo.status === ReportStatus.Failed ||
          processInfo.status === ReportStatus.Unavailable)
      ) {
        setIsError(true);
        setIsLoading(false);
        clearInterval(intervalId);
        return;
      }

      try {
        await getProcessInfo(controller.signal);
        setIsError(false);
      } catch (e) {
        // Disregard a canceled error
        if ((e as Error).message === "canceled") return;

        setIsError(true);
        setIsLoading(false);
        // if no gpa data exists for the progression analysis,
        // stop refetching data and exit the loop
        clearInterval(intervalId);
        return;
      }
    }, intervalTime);

    return () => {
      controller.abort();
      clearInterval(intervalId);
    };
  }, [enabled, processInfo, getProcessInfo, getGPAData]);

  return {
    isLoading,
    isError,
    isSkipped,
    analysisData,
  };
};

export default useGPAGeneration;
